import { Alert, Badge, Button, Group, Modal, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { PaperRowControl } from '../../components/ThePaperItself';
import { enumColor, enumLabel, enumOptions } from '../../lib/enums';
import { formatDate, formatMoney } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { BillSubject, billSubject } from './billSubject';
import type { BillVendor } from './RecordBillForm';
import { RecordCreditButton } from './RecordCreditForm';

const DISCREPANCIES_PATH = '/admin/v1/suppliers/bill-discrepancies';
const BILLS_PATH = '/admin/v1/suppliers/supplier-invoices';
const PAYABLES_PATH = '/admin/v1/suppliers/payables';
const SUPPLIERS_PATH = '/admin/v1/suppliers/suppliers';

/** One bill whose paper and delivery do not agree, as the server works it out. */
interface Disagreement {
  invoice: {
    id: string;
    supplierId: string;
    invoiceNumber?: string | null;
    description?: string | null;
    receiptId?: string | null;
    invoiceDate: string;
    currency: string;
    totalMinor: number;
    receivedTotalMinor?: number | null;
    discrepancyReason?: string | null;
    discrepancyNote?: string | null;
    /** The photographed bill. This is the list where somebody most wants to go and look at it. */
    sourceDocumentId?: string | null;
  };
  supplierName: string | null;
  creditedMinor: number;
  outstandingMinor: number;
  /** Signed. Positive: they billed for more than arrived. Negative: more arrived than was billed. */
  discrepancyMinor: number;
  /** How much of it nobody has answered yet. */
  discrepancyOpenMinor: number;
}

/**
 * The bills that do not agree with what came off the boat.
 *
 * A discrepancy nobody can find is the same as no discrepancy. Ten cases invoiced against seven
 * delivered is not an error to be corrected at the door — it is a real state of affairs that lasts
 * for weeks, until the vendor credits it, sends the missing cases, or turns out to have been right.
 * What makes it survivable is that it sits somewhere the owner looks, with the money and the reason
 * on it, until one of those things happens. Without this it would be a column on a row nobody reads
 * and a surprise on a statement six months later.
 *
 * It sits above Bills to pay rather than in a tab of its own because it is about the same bills and
 * has the same audience, and because a tab that is empty most weeks is a tab that stops being
 * opened. With nothing to show it renders nothing: the answer to "are there any" is the heading
 * being there or not, and a permanent empty table under a permanent heading is furniture.
 */
export function BillsThatDisagreeSection({ supplierId }: { supplierId?: string }) {
  const rows = useQuery({
    queryKey: listKey(DISCREPANCIES_PATH),
    queryFn: () => rawApi<Disagreement[]>('GET', DISCREPANCIES_PATH),
  });
  // The vendor a credit would be recorded against: their own money and their own terms come off
  // this record, exactly as they do when the credit is started from the vendor's own page.
  const vendors = useQuery({
    queryKey: listKey(SUPPLIERS_PATH),
    queryFn: () => rawApi<BillVendor[]>('GET', SUPPLIERS_PATH),
    staleTime: 60_000,
  });
  const [settling, setSettling] = useState<Disagreement | null>(null);

  const mine = (rows.data ?? []).filter((r) => !supplierId || r.invoice.supplierId === supplierId);

  if (rows.isLoading || mine.length === 0) return null;

  return (
    <>
      <Stack gap="xs" mb="xl">
        <Group gap="sm" align="baseline">
          <Text fw={600}>Bills that do not agree with the delivery</Text>
          <Badge color="orange" variant="light">
            {mine.length}
          </Badge>
        </Group>
        <Text size="sm" c="dimmed">
          What they billed is not what turned up. The bill is recorded as it is printed — that is what they
          will chase you for — and the shelf shows what arrived. Each of these is waiting on a credit note, on
          the missing goods, or on somebody agreeing the bill was right.
        </Text>

        {/* 720 was the floor, and seven columns do not fit in it: the last two are a sentence
            saying what is wrong and the two buttons that put it right, and at 720 the sentence
            compressed to 53 pixels — one word per line, six lines tall — with the buttons clipped
            off the edge beside it. A table that has scrolled all the way and still cannot be read
            is worse than one the reader knows to scroll, so the floor is the width the row actually
            needs. */}
        <Table.ScrollContainer minWidth={1100}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Supplier</Table.Th>
                <Table.Th>What for</Table.Th>
                <Table.Th>Dated</Table.Th>
                <Table.Th ta="right">They billed</Table.Th>
                <Table.Th ta="right">What arrived</Table.Th>
                <Table.Th>The difference</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {mine.map((row) => {
                const currency = row.invoice.currency;
                const vendor = (vendors.data ?? []).find((v) => v.id === row.invoice.supplierId);
                const theyOwe = row.discrepancyOpenMinor > 0;
                return (
                  <Table.Tr key={row.invoice.id}>
                    <Table.Td>{row.supplierName ?? '—'}</Table.Td>
                    <Table.Td>
                      <Stack gap={2} align="flex-start">
                        <BillSubject bill={row.invoice} />
                        {/* Ten on the paper against seven off the boat is settled by reading the
                            paper. This is the one list where the photograph is not a nicety: the
                            argument with the vendor is about what was written down. */}
                        <PaperRowControl
                          documentId={row.invoice.sourceDocumentId}
                          attachPath={`${BILLS_PATH}/${row.invoice.id}/document`}
                          subject={billSubject(row.invoice)}
                          invalidate={[
                            listKey(DISCREPANCIES_PATH),
                            listKey(PAYABLES_PATH),
                            listKey(BILLS_PATH),
                          ]}
                        />
                      </Stack>
                    </Table.Td>
                    <Table.Td>{formatDate(row.invoice.invoiceDate)}</Table.Td>
                    <Table.Td ta="right" fw={600}>
                      {formatMoney(row.invoice.totalMinor, currency)}
                    </Table.Td>
                    <Table.Td ta="right">
                      {typeof row.invoice.receivedTotalMinor === 'number'
                        ? formatMoney(row.invoice.receivedTotalMinor, currency)
                        : '—'}
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="sm" fw={700} c={theyOwe ? 'orange.8' : 'blue.8'}>
                          {/* The sign in words, because a minus in a column of money is the one
                              thing a reader is guaranteed to misread at a glance. */}
                          {theyOwe
                            ? `${formatMoney(row.discrepancyOpenMinor, currency)} billed and not delivered`
                            : `${formatMoney(-row.discrepancyOpenMinor, currency)} delivered and not billed`}
                        </Text>
                        {row.invoice.discrepancyReason && (
                          <Badge
                            size="sm"
                            variant="light"
                            color={
                              enumColor('billDiscrepancyReason', row.invoice.discrepancyReason) ?? 'gray'
                            }
                            /* This one is a sentence rather than a status word — "They billed for
                               goods that did not arrive" — in a column sharing a row with two
                               buttons, so it WRAPS rather than taking a column's width of its own.
                               Every badge already shows its whole label (see the Badge default in
                               main.tsx); this one needs the extra line to do it in. */
                            h="auto"
                            py={2}
                            styles={{ label: { whiteSpace: 'normal' } }}
                          >
                            {enumLabel('billDiscrepancyReason', row.invoice.discrepancyReason)}
                          </Badge>
                        )}
                        {row.invoice.discrepancyNote && (
                          <Text size="xs" c="dimmed">
                            {row.invoice.discrepancyNote}
                          </Text>
                        )}
                        {/* Part of it already came back. Said out loud, because otherwise the
                            figure above is simply smaller than it was and nothing says why. */}
                        {theyOwe && row.creditedMinor > 0 && (
                          <Text size="xs" c="dimmed">
                            {formatMoney(row.creditedMinor, currency)} credited back so far
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" justify="flex-end" wrap="nowrap">
                        {/* Only where a credit is the remedy: goods they forgot to charge for are
                            not settled by them crediting you more money. */}
                        {theyOwe && vendor && (
                          <RecordCreditButton
                            vendor={vendor}
                            againstBillId={row.invoice.id}
                            label="Record their credit"
                          />
                        )}
                        <Button size="compact-sm" variant="subtle" onClick={() => setSettling(row)}>
                          Settle it another way
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Stack>

      {settling && <SettleDisagreementForm row={settling} onClose={() => setSettling(null)} />}
    </>
  );
}

/**
 * Closing a disagreement that no credit note is going to close.
 *
 * They sent the missing cases on Thursday's boat; the bill turned out to be right; the rest is
 * coming on a later invoice. None of those can be worked out from anything on file — a person knows
 * and nothing else does — so a person says which, and that is what takes the bill off the list.
 *
 * The difference itself is not erased. It happened, the vendor's statement will still show it, and
 * a row that quietly forgot it would be the same row that could not explain itself in six months.
 */
function SettleDisagreementForm({ row, onClose }: { row: Disagreement; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [resolution, setResolution] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const settle = useMutation({
    mutationFn: () =>
      rawApi('POST', `${BILLS_PATH}/${row.invoice.id}/discrepancy`, {
        resolution,
        note: note.trim() || null,
      }),
    onSuccess: () => {
      for (const key of [DISCREPANCIES_PATH, PAYABLES_PATH]) {
        void queryClient.invalidateQueries({ queryKey: listKey(key) });
      }
      void queryClient.invalidateQueries({
        queryKey: listKey(BILLS_PATH, { supplierId: row.invoice.supplierId }),
      });
      onClose();
      notifyDone(
        'Marked as dealt with',
        `That bill is off the list. It is still recorded as ${formatMoney(row.invoice.totalMinor, row.invoice.currency)}, which is what they billed.`,
      );
    },
    onError: (err: Error) => notifyError(err, 'mark this as dealt with'),
  });

  const submit = () => {
    if (!resolution) return setError('Say how it was settled');
    setError(null);
    settle.mutate();
  };

  return (
    <Modal opened onClose={onClose} title="How was this settled?" size="lg">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {row.supplierName ?? 'This supplier'} billed{' '}
          {formatMoney(row.invoice.totalMinor, row.invoice.currency)} against{' '}
          {typeof row.invoice.receivedTotalMinor === 'number'
            ? formatMoney(row.invoice.receivedTotalMinor, row.invoice.currency)
            : 'what arrived'}{' '}
          of goods, dated {formatDate(row.invoice.invoiceDate)}.
        </Text>

        <Alert color="blue" variant="light" title="If they sent you a credit note, record that instead">
          A credit takes the difference off what you owe them. This is only for the endings a credit note does
          not cover, and it changes no money.
        </Alert>

        <Select
          label="How it was settled"
          required
          data={enumOptions('billDiscrepancyResolution')}
          value={resolution}
          onChange={setResolution}
          placeholder="Pick one"
          comboboxProps={{ withinPortal: true }}
          disabled={settle.isPending}
        />

        {/*
         * The one ending that is also an ACTION, and the only one where saying it can leave the
         * business worse off than saying nothing.
         *
         * The other two change nothing but this list: agreeing the bill was right, or waiting for a
         * further invoice, move no goods and no money by definition. "They sent what was missing"
         * does — three cases came over on a boat and are standing in the bodega. Recorded here it is
         * a sentence: the row leaves the list, the shelf never hears about it, and stock is short by
         * three cases of beer that are physically present. Recorded as a delivery against this bill
         * it is the real thing: the goods go on the shelf, what has arrived against the bill goes up
         * by what they came to, and the difference closes by arithmetic with nothing to say.
         *
         * So the sentence is kept — the goods may have been put away days ago, or against another
         * bill, and then it is the only way to say so — and the screen names the better door when
         * they have not. The credit ending has carried this signpost since it was written; this one
         * is the same hazard with stock instead of money on the other side of it.
         */}
        {resolution === 'theySentTheGoods' && (
          <Alert color="orange" variant="light" title="Have those goods been put away yet?">
            If they are still on the trolley, close this and record them as a delivery from{' '}
            {row.supplierName ?? 'this supplier'} instead, against this same bill — that puts them on the
            shelf and closes the difference on its own. Use this only when they were already counted in.
          </Alert>
        )}

        <TextInput
          label="What happened"
          description="For whoever reads this against their statement later"
          placeholder="Rest came over on Thursday’s boat"
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          disabled={settle.isPending}
        />

        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={settle.isPending}>
            Cancel
          </Button>
          <Button loading={settle.isPending} onClick={submit} disabled={!resolution}>
            Mark it dealt with
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
